/**
 * Column — transparent grid item wrapper for Canvas v2.0
 *
 * Architectural pattern (, ):
 *   ALL stylable / responsive-overridable properties (padding, background,
 *   border, border-radius, box-shadow) are emitted into a generated <style>
 *   block keyed by the column's id — NEVER as inline styles. This means:
 *
 *     • Desktop rule:  #col-{id} { padding: X; background: Y; ... }
 *     • Tablet rule:   @media (min-width: 769px) and (max-width: 1024px)
 *                        { #col-{id} { padding: tabletX; ... } }
 *     • Mobile rule:   @media (max-width: 768px)
 *                        { #col-{id} { padding: mobileX; ... } }
 *
 *   All three sit at the same selector specificity (0,1,0) inside the same
 *   <style> tag. The mobile/tablet rules come AFTER the desktop rule in
 *   source order, so the cascade resolves them naturally — NO !important
 *   needed. This is the project's iron-clad CSS rule.
 *
 * Layout-only props (display/flex/alignItems/position/etc.) stay inline —
 * they aren't responsive-overridable in the editor and aren't visible-style
 * properties that the user adjusts per breakpoint.
 *
 * Responsive overrides accepted (any subset is fine — missing ones inherit
 * from desktop):
 *   paddingMobile           / mobilePadding           : { top, right, bottom, left }
 *   paddingTablet           / tabletPadding           : { top, right, bottom, left }
 *   mobileBackgroundColor   / backgroundColorMobile   : string
 *   tabletBackgroundColor   / backgroundColorTablet   : string
 *   mobileBorder            / borderMobile            : string  (CSS shorthand)
 *   tabletBorder            / borderTablet            : string
 *   mobileBorderRadius      / borderRadiusMobile      : string|number
 *   tabletBorderRadius      / borderRadiusTablet      : string|number
 *   mobileBoxShadow         / boxShadowMobile         : string
 *   tabletBoxShadow         / boxShadowTablet         : string
 *
 * PDP responsive props.
 */
import React from 'react';
import type { BlockComponentProps } from '../../renderer/BlockRegistry';

type BoxSides = { top?: number; right?: number; bottom?: number; left?: number };

/**
 * Format a padding/margin value into a CSS declaration string. Accepts:
 *   - string  -> used verbatim (CSS shorthand or var() expression)
 *   - object  -> expanded into 4-side declarations
 * Returns the CSS lines (e.g. 'padding-top: 24px; padding-left: 16px;').
 * Returns empty string when val is null/undefined.
 */
function formatBoxSides(val: unknown, prop: 'padding' | 'margin'): string {
  if (val == null) return '';
  if (typeof val === 'string') {
    return `${prop}: ${val};`;
  }
  if (typeof val === 'object') {
    const v = val as BoxSides;
    const parts: string[] = [];
    if (v.top != null) parts.push(`${prop}-top: ${v.top}px;`);
    if (v.right != null) parts.push(`${prop}-right: ${v.right}px;`);
    if (v.bottom != null) parts.push(`${prop}-bottom: ${v.bottom}px;`);
    if (v.left != null) parts.push(`${prop}-left: ${v.left}px;`);
    return parts.join(' ');
  }
  return '';
}

/**
 * Format a single declaration if value is set; else empty string.
 */
function decl(prop: string, val: unknown): string {
  if (val == null || val === '') return '';
  if (typeof val === 'number') return `${prop}: ${val}px;`;
  if (typeof val === 'string') return `${prop}: ${val};`;
  return '';
}

/**
 * Format border-radius — accepts numbers, px-strings, var() expressions.
 */
function formatRadius(val: unknown): string {
  if (val == null || val === '') return '';
  if (typeof val === 'number') return `${val}px`;
  if (typeof val === 'string') return val;
  return '';
}

/**
 * Stable id from a column id — falls back to a deterministic short hash if
 * absent, so server and client both produce the same selector.
 */
const asStr = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
const asNum = (v: unknown): number | undefined => (typeof v === 'number' ? v : undefined);
const asStrOrNum = (v: unknown): string | number | undefined => (typeof v === 'string' || typeof v === 'number' ? v : undefined);

function colSelectorId(rawId: unknown, fallback: string): string {
  if (typeof rawId === 'string' && rawId) return `col-${rawId}`;
  if (typeof rawId === 'number') return `col-${rawId}`;
  return `col-x-${fallback}`;
}

export const Column: React.FC<BlockComponentProps & { 'data-block-type'?: string, 'data-block-id'?: string }> = ({ data, children, style, className, 'data-block-type': dataBlockType, 'data-block-id': dataBlockId }) => {
  // Stable fallback id: prefer the block id passed in by BlockRenderer, then
  // React.useId(). Math.random() previously differed between SSR and client
  // and stranded the <style> selector pointing at an id the client never made.
  const reactId = React.useId().replace(/:/g, '');
  const colId = colSelectorId(data?.id, dataBlockId || reactId);

  // Responsive overrides (accept BOTH naming conventions)
  const padMobile  = (data?.paddingMobile  ?? data?.mobilePadding)  as BoxSides | string | undefined;
  const padTablet  = (data?.paddingTablet  ?? data?.tabletPadding)  as BoxSides | string | undefined;
  const mobBg      = (data?.mobileBackgroundColor ?? data?.backgroundColorMobile) as string | undefined;
  const tabBg      = (data?.tabletBackgroundColor ?? data?.backgroundColorTablet) as string | undefined;
  const mobBorder  = (data?.mobileBorder   ?? data?.borderMobile)   as string | undefined;
  const tabBorder  = (data?.tabletBorder   ?? data?.borderTablet)   as string | undefined;
  const mobRadius  = (data?.mobileBorderRadius ?? data?.borderRadiusMobile) as string | number | undefined;
  const tabRadius  = (data?.tabletBorderRadius ?? data?.borderRadiusTablet) as string | number | undefined;
  const mobShadow  = (data?.mobileBoxShadow ?? data?.boxShadowMobile) as string | undefined;
  const tabShadow  = (data?.tabletBoxShadow ?? data?.boxShadowTablet) as string | undefined;

  // Build generated CSS for this column
  const desktopRule = [
    formatBoxSides(data?.padding, 'padding'),
    decl('background-color', data?.backgroundColor),
    data?.backgroundImage ? decl('background-image', data.backgroundImage) : '',
    decl('border', data?.border),
    decl('border-radius', formatRadius(data?.borderRadius)),
    decl('box-shadow', data?.boxShadow),
  ].filter(Boolean).join(' ');

  const tabletRule = [
    formatBoxSides(padTablet, 'padding'),
    decl('background-color', tabBg),
    decl('border', tabBorder),
    decl('border-radius', formatRadius(tabRadius)),
    decl('box-shadow', tabShadow),
  ].filter(Boolean).join(' ');

  const mobileRule = [
    formatBoxSides(padMobile, 'padding'),
    decl('background-color', mobBg),
    decl('border', mobBorder),
    decl('border-radius', formatRadius(mobRadius)),
    decl('box-shadow', mobShadow),
  ].filter(Boolean).join(' ');

  // Visibility (responsive show/hide) — preserved from previous impl
  interface ColVisibility { mobile?: boolean; tablet?: boolean; desktop?: boolean }
  const visibility: ColVisibility | undefined = (data?.visibility as ColVisibility | undefined) ?? (data?.settings as { visibility?: ColVisibility } | undefined)?.visibility;
  let visibilityCSS = '';
  if (visibility) {
    if (visibility.mobile === false) {
      visibilityCSS += `@media (max-width: 768px) { #${colId} { display: none; } }`;
    }
    if (visibility.tablet === false) {
      visibilityCSS += `@media (min-width: 769px) and (max-width: 1024px) { #${colId} { display: none; } }`;
    }
    if (visibility.desktop === false) {
      visibilityCSS += `@media (min-width: 1025px) { #${colId} { display: none; } }`;
    }
  }
  // Visibility uses the same selector specificity but comes AFTER all the
  // other rules in source order, so display:none wins via cascade ordering
  // — no !important needed.

  // Assemble the <style> block. Empty rules are omitted so we don't emit
  // bare `#col-foo { }` selectors.
  const cssBlocks: string[] = [];
  if (desktopRule) cssBlocks.push(`#${colId} { ${desktopRule} }`);
  if (tabletRule)  cssBlocks.push(`@media (min-width: 769px) and (max-width: 1024px) { #${colId} { ${tabletRule} } }`);
  if (mobileRule)  cssBlocks.push(`@media (max-width: 768px) { #${colId} { ${mobileRule} } }`);
  if (visibilityCSS) cssBlocks.push(visibilityCSS);
  const generatedCss = cssBlocks.join('\n');

  // Layout-only inline styles (NOT responsive-overridable)
  const horizontalToAlignItems: Record<string, string> = {
    'left': 'flex-start',
    'center': 'center',
    'right': 'flex-end',
    'stretch': 'stretch'
  };
  const horizontalAlignRaw = asStr(data?.horizontalAlign);
  const horizontalAlign: string | undefined = horizontalAlignRaw ? horizontalToAlignItems[horizontalAlignRaw] : undefined;
  const needsFlex = !!data?.gap || !!horizontalAlign;
  const isAlignSelf: string | undefined = !needsFlex ? horizontalAlignRaw : undefined;

  const positionTop = asNum(data?.positionTop);
  const colStyle: React.CSSProperties = {
    // Width caps — `maxWidth` stays as a cap inside the grid track. `width` is
    // INTENTIONALLY NOT inline. The parent row reads `data.width` to build its
    // grid-template-columns (e.g. '1fr 1fr 400px') — that's the single source
    // of truth for column sizing. Setting width inline AS WELL pinned the
    // column to the desktop track at mobile (inline 1,0,0,0 beats @media), so
    // 400px-fixed columns refused to stretch when stackOnMobile collapsed the
    // grid to 1fr. The column's div now fills its grid track via implicit 100%.
    maxWidth: asStrOrNum(data?.maxWidth) || undefined,
    minWidth: asStrOrNum(data?.minWidth) ?? 0, // default 0 (prevents grid blowout); settable
    maxHeight: asStrOrNum(data?.maxHeight) || undefined,
    minHeight: asStrOrNum(data?.minHeight) || undefined,
    display: (asStr(data?.display)) || (needsFlex ? 'flex' : undefined),
    flexDirection: (asStr(data?.flexDirection) as React.CSSProperties['flexDirection']) || (needsFlex ? 'column' : undefined),
    gap: asStrOrNum(data?.gap) || undefined,
    alignItems: asStr(data?.alignItems) || horizontalAlign,
    justifyContent: asStr(data?.justifyContent) || undefined,
    textAlign: isAlignSelf === 'center' ? 'center' : (isAlignSelf === 'right' ? 'right' : undefined),
    position: data?.position === 'sticky' ? 'sticky' : undefined,
    top: data?.position === 'sticky' ? `${positionTop ?? 32}px` : (positionTop != null ? `${positionTop}px` : undefined),
    zIndex: asNum(data?.zIndex) || undefined,
    // When sticky, force align-self: start — CSS Grid stretches children by
    // default which creates a column equal to the row height, preventing sticky
    // from having any scroll distance. flex-start gives it a natural height.
    alignSelf: data?.position === 'sticky' ? 'start'
      : data?.alignSelf ? data.alignSelf as React.CSSProperties['alignSelf']
        : data?.verticalAlign === 'middle' ? 'center'
          : data?.verticalAlign === 'bottom' ? 'end'
            : undefined,
    ...style,
  };

  return (
    <>
      {generatedCss && <style dangerouslySetInnerHTML={{ __html: generatedCss }} />}
      <div id={colId} style={colStyle} className={`is-layout-container empty:hidden ${typeof data?.customClasses === 'string' ? data.customClasses : ''} ${className ?? ''}`.trim()} data-block-type={dataBlockType} data-block-id={dataBlockId}>
        {children}
      </div>
    </>
  );
};
